import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../styleguide/Logo';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationPanel from '../notifications/NotificationPanel';
import apiService from '../../services/apiService';
import offlineDb from '../../services/offlineDb';


/**
 * Barra de navegación principal de la aplicación
 *
 * Incluye menú desktop con dropdowns y menú móvil hamburguesa.
 *
 * @returns {JSX.Element}
 */
function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();
  const { userName, hasPermission } = useAuth();
  const { unreadCount } = useNotifications();

  /**
   * Cierra sesión limpiando auth state y redirigiendo al inicio
   */
  const handleLogout = async () => {
    try {
      apiService.clearAuthState();
      apiService.removeToken();
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.allSettled(names.map(n => caches.delete(n)));
      }
      await offlineDb.clearAll();
    } catch (err) {
      console.error('Cleanup error:', err);
    } finally {
      window.dispatchEvent(new Event('authChange'));
      window.location.replace('/');
    }
  };

  /**
   * Alterna visibilidad del menú móvil
   */
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  /**
   * Muestra dropdown al pasar mouse
   * @param {string} dropdownName - Nombre del dropdown
   */
  const handleMouseEnter = (dropdownName) => {
    setHoveredDropdown(dropdownName);
    setNotifOpen(false);
  };

  /**
   * Oculta dropdown al salir mouse
   */
  const handleMouseLeave = () => {
    setHoveredDropdown(null);
  };

  /**
   * Verifica si una ruta está activa
   * @param {string} path - Ruta a verificar
   * @returns {boolean}
   */
  const isActive = (path) => {
    return location.pathname === path;
  };

  /**
   * Verifica si alguna ruta del grupo está activa
   * @param {Array} paths - Rutas a verificar
   * @returns {boolean}
   */
  const isAnyActive = (paths) => {
    return paths.some(path => location.pathname === path);
  };

  // Items principales del menú
  const menuItems = [
    { path: '/cotizaciones', label: 'Cotizaciones', icon: 'fa-file-invoice-dollar', permission: 'cotizaciones.read' }
  ];

  // Items de Equipos (dropdown)
  const equiposItems = [
    { path: '/equipos', label: 'Listado Equipos', icon: 'fa-cogs', permission: 'equipos.read' },
    { path: '/asistencia', label: 'Asistencia', icon: 'fa-clipboard-check', permission: 'asistencias_equipo.read' },
    { path: '/hallazgos', label: 'Hallazgos', icon: 'fa-flag', permission: 'hallazgos.read_all' },
    { path: '/mis-hallazgos', label: 'Mis Hallazgos', icon: 'fa-binoculars', permission: 'hallazgos.read' }
  ];

  // Items de mantención (dropdown)
  const mantencionItems = [
    { path: '/programas', label: 'Programas', icon: 'fa-calendar-alt', permission: 'programas.read' },
    { path: '/ordenes-trabajo', label: 'Órdenes de Trabajo', icon: 'fa-wrench', permission: 'ordenes_trabajo.read_all' },
    { path: '/calendario-ot', label: 'Calendario OTs', icon: 'fa-calendar', permission: 'ordenes_trabajo.read_all' },
    { path: '/mis-ordenes-trabajo', label: 'Mis OTs', icon: 'fa-hard-hat', permission: 'ordenes_trabajo.read' },
    { path: '/analytics', label: 'Reportes', icon: 'fa-chart-line', permission: 'reportes.analytics' }
  ];

  // Items de configuración (dropdown)
  const configItems = [
    { path: '/clientes', label: 'Clientes', icon: 'fa-users', permission: 'clientes.read' },
    { path: '/ubicaciones', label: 'Ubicaciones', icon: 'fa-map-marker-alt', permission: 'ubicaciones.read' },
    { path: '/categorias', label: 'Categorías', icon: 'fa-tags', permission: 'categorias.read' },
    { path: '/tipos-documento', label: 'Tipos de Documento', icon: 'fa-file-alt', permission: 'tipos_documento.read' },
    { path: '/plantillas-mantenimiento', label: 'Plantillas Mantención', icon: 'fa-clipboard-list', permission: 'plantillas_mantenimiento.read' }
  ];

  // Items visibles según permisos reales del usuario
  const visibleMenuItems = menuItems.filter(item => !item.permission || hasPermission(item.permission));
  const visibleEquiposItems = equiposItems.filter(item => hasPermission(item.permission));
  const visibleMantencionItems = mantencionItems.filter(item => hasPermission(item.permission));
  const visibleConfigItems = configItems.filter(item => hasPermission(item.permission));

  // Items personales del usuario
  const userItems = [
    { path: '/mi-galeria', label: 'Mi Galería', icon: 'fa-images', permission: 'galeria.read' }
  ];
  const visibleUserItems = userItems.filter(item => hasPermission(item.permission));

  // Items de administración — visibles según permisos del usuario
  const adminItems = [
    { path: '/admin/usuarios', label: 'Usuarios', icon: 'fa-users', permission: 'usuarios.read' },
    { path: '/admin/roles', label: 'Gestión de Roles', icon: 'fa-user-shield', permission: 'roles.read' },
    { path: '/admin/permisos', label: 'Asignación de Permisos', icon: 'fa-key', permission: 'permisos.catalogo' }
  ];
  const visibleAdminItems = adminItems.filter(item => hasPermission(item.permission));

  return (
    <nav className="navbar">
      <div className="container navbar-container">
        <div className="navbar-brand">
          <Link to="/home" className="navbar-brand-link">
            <Logo size="small" />
          </Link>
        </div>

        <ul className="navbar-menu">
          {/* Dropdown de Equipos */}
          {visibleEquiposItems.length > 0 && (
            <li
              className="navbar-item navbar-dropdown"
              onMouseEnter={() => handleMouseEnter('equipos')}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`navbar-link navbar-link-dropdown ${isAnyActive(visibleEquiposItems.map(i => i.path)) ? 'active' : ''}`}>
                <i className="fas fa-cogs"></i>
                <span>Equipos</span>
                <i className={`fas fa-caret-down ${hoveredDropdown === 'equipos' ? 'rotated' : ''}`}></i>
              </button>
              {hoveredDropdown === 'equipos' && (
                <div className="navbar-dropdown-menu">
                  {visibleEquiposItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`navbar-dropdown-link ${isActive(item.path) ? 'active' : ''}`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </li>
          )}

          {visibleMenuItems.map(item => (
            <li key={item.path} className="navbar-item">
              <Link
                to={item.path}
                className={`navbar-link ${isActive(item.path) ? 'active' : ''}`}
              >
                <i className={`fas ${item.icon}`}></i>
                <span>{item.label}</span>
              </Link>
            </li>
          ))}

          {/* Dropdown de Mantención */}
          {visibleMantencionItems.length > 0 && (
            <li
              className="navbar-item navbar-dropdown"
              onMouseEnter={() => handleMouseEnter('mantencion')}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`navbar-link navbar-link-dropdown ${isAnyActive(visibleMantencionItems.map(i => i.path)) ? 'active' : ''}`}>
                <i className="fas fa-tools"></i>
                <span>Mantención</span>
                <i className={`fas fa-caret-down ${hoveredDropdown === 'mantencion' ? 'rotated' : ''}`}></i>
              </button>
              {hoveredDropdown === 'mantencion' && (
                <div className="navbar-dropdown-menu">
                  {visibleMantencionItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`navbar-dropdown-link ${isActive(item.path) ? 'active' : ''}`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </li>
          )}

          {/* Dropdown de Configuración */}
          {visibleConfigItems.length > 0 && (
            <li
              className="navbar-item navbar-dropdown"
              onMouseEnter={() => handleMouseEnter('config')}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`navbar-link navbar-link-dropdown ${isAnyActive(visibleConfigItems.map(i => i.path)) ? 'active' : ''}`}>
                <i className="fas fa-cog"></i>
                <span>Configuración</span>
                <i className={`fas fa-caret-down ${hoveredDropdown === 'config' ? 'rotated' : ''}`}></i>
              </button>
              {hoveredDropdown === 'config' && (
                <div className="navbar-dropdown-menu">
                  {visibleConfigItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`navbar-dropdown-link ${isActive(item.path) ? 'active' : ''}`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </li>
          )}

          {/* Dropdown de Perfil — incluye opciones de administración si el usuario tiene acceso */}
          <li
            className="navbar-item navbar-dropdown"
            onMouseEnter={() => handleMouseEnter('perfil')}
            onMouseLeave={handleMouseLeave}
          >
            <button className={`navbar-link navbar-link-dropdown ${isAnyActive([...visibleUserItems, ...visibleAdminItems].map(i => i.path)) ? 'active' : ''}`}>
              <i className="fas fa-user-circle"></i>
              <span>{userName}</span>
              <i className={`fas fa-caret-down ${hoveredDropdown === 'perfil' ? 'rotated' : ''}`}></i>
            </button>
            {hoveredDropdown === 'perfil' && (
              <div className="navbar-dropdown-menu">
                {visibleUserItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`navbar-dropdown-link ${isActive(item.path) ? 'active' : ''}`}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                ))}
                {visibleAdminItems.length > 0 && (
                  <>
                    {visibleUserItems.length > 0 && <div className="navbar-dropdown-divider" />}
                    {visibleAdminItems.map(item => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`navbar-dropdown-link ${isActive(item.path) ? 'active' : ''}`}
                      >
                        <i className={`fas ${item.icon}`}></i>
                        <span>{item.label}</span>
                      </Link>
                    ))}
                    <div className="navbar-dropdown-divider" />
                  </>
                )}
                <button onClick={handleLogout} className="navbar-dropdown-link navbar-button-as-link">
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Cerrar sesion</span>
                </button>
              </div>
            )}
          </li>
        </ul>

        <div className="navbar-notification-bell" style={{ position: 'relative' }}>
          <button
            className="navbar-link"
            onClick={() => { setNotifOpen(prev => !prev); setHoveredDropdown(null); }}
            title="Notificaciones"
          >
            <i className="fas fa-bell"></i>
            {unreadCount > 0 && <span className="notif-count">{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
          <NotificationPanel isOpen={notifOpen} onToggle={setNotifOpen} />
        </div>

        <button
          className={`hamburger ${mobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <div className={`navbar-mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
        <ul className="navbar-mobile-list">
          {/* Sección Equipos */}
          {visibleEquiposItems.length > 0 && (
            <>
              <li className="navbar-mobile-item navbar-mobile-divider">
                <span className="navbar-mobile-section-title">
                  <i className="fas fa-cogs"></i> Equipos
                </span>
              </li>
              {visibleEquiposItems.map(item => (
                <li key={item.path} className="navbar-mobile-item">
                  <Link
                    to={item.path}
                    className={`navbar-mobile-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </>
          )}

          {visibleMenuItems.map(item => (
            <li key={item.path} className="navbar-mobile-item">
              <Link
                to={item.path}
                className={`navbar-mobile-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className={`fas ${item.icon}`}></i>
                <span>{item.label}</span>
              </Link>
            </li>
          ))}

          {/* Separador y sección Mantención */}
          {visibleMantencionItems.length > 0 && (
            <>
              <li className="navbar-mobile-item navbar-mobile-divider">
                <span className="navbar-mobile-section-title">
                  <i className="fas fa-tools"></i> Mantención
                </span>
              </li>
              {visibleMantencionItems.map(item => (
                <li key={item.path} className="navbar-mobile-item">
                  <Link
                    to={item.path}
                    className={`navbar-mobile-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </>
          )}

          {/* Separador y sección Configuración */}
          {visibleConfigItems.length > 0 && (
            <>
              <li className="navbar-mobile-item navbar-mobile-divider">
                <span className="navbar-mobile-section-title">
                  <i className="fas fa-cog"></i> Configuración
                </span>
              </li>
              {visibleConfigItems.map(item => (
                <li key={item.path} className="navbar-mobile-item">
                  <Link
                    to={item.path}
                    className={`navbar-mobile-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </>
          )}

          {/* Mi Galería en sección personal */}
          {visibleUserItems.length > 0 && (
            <>
              {visibleUserItems.map(item => (
                <li key={item.path} className="navbar-mobile-item">
                  <Link
                    to={item.path}
                    className={`navbar-mobile-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </>
          )}

          {/* Separador y sección Administración — visible según permisos */}
          {visibleAdminItems.length > 0 && (
            <>
              <li className="navbar-mobile-item navbar-mobile-divider">
                <span className="navbar-mobile-section-title">
                  <i className="fas fa-user-shield"></i> Administración
                </span>
              </li>
              {visibleAdminItems.map(item => (
                <li key={item.path} className="navbar-mobile-item">
                  <Link
                    to={item.path}
                    className={`navbar-mobile-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </>
          )}

          {/* Separador y Cerrar sesión */}
          <li className="navbar-mobile-item navbar-mobile-divider"></li>
          <li className="navbar-mobile-item">
            <button
              onClick={handleLogout}
              className="navbar-mobile-link navbar-button-as-link"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Cerrar sesion</span>
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
